import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import ReactDOM from "react-dom";
import FilterPanel from "./FilterPanel";
import ColumnDateFilter from "./ColumnDateFilter";
import SkSelect from "./SkSelect";
import {
  isWithinDateRange,
  matchesAdvancedFilters,
  matchTextOperator,
  TEXT_OPERATORS,
  DEFAULT_TEXT_OPERATOR,
  getOperatorMeta,
} from "../utils/filterUtils";
import { convert24To12 } from "../utils/common";

// A "select" header filter can declare its choices either as ready-made
// `filterOptions` ([{value,label}], already carrying its own "All") or as a
// plain `options` string list — normalise both, and make sure there is always
// an empty "All" entry so the filter can be cleared again.
/* A column whose values repeat from a small set is a CATEGORY, and a category
   wants a dropdown of what is actually there — not a text box you have to
   already know the answer to type into.
 *
 * Detected rather than declared. Declaring it means every column on every grid
 * has to be hand-labelled and then kept labelled, and the ones that got missed
 * are exactly the ones people complain about: Query Type sat above four
 * repeating values and offered a blank box.
 *
 * The test is that values REPEAT and there are few of them. Both halves
 * matter — Task Name has few distinct values on a five-row page too, and
 * turning it into a dropdown the moment the grid is nearly empty would be
 * worse than leaving it alone. Requiring at least twice as many rows as
 * distinct values means a genuine category (a handful of statuses over fifty
 * rows) qualifies and a list of names never does.
 *
 * A column that declares filterType keeps it: an explicit choice is a decision
 * somebody made, and guessing over the top of it is not an improvement. */
const AUTO_SELECT_MAX = 25;   // more than this and a dropdown is worse than typing
const AUTO_SELECT_MIN_ROWS = 8;
const AUTO_SELECT_MAX_LEN = 40; // a sentence is not a category

const categoricalOptions = (col, rows) => {
  if (col?.filterType || col?.options?.length || col?.filterOptions?.length) return null;
  if (!Array.isArray(rows) || rows.length < AUTO_SELECT_MIN_ROWS) return null;
  if (col?.kind === "duration" || col?.numeric) return null;

  const seen = new Set();
  let counted = 0;

  for (const r of rows) {
    const raw = col?.searchValue ? col.searchValue(r) : r?.[col?.key];
    if (raw === null || raw === undefined) continue;
    if (typeof raw === "object") return null;   // renders to a node, not a value

    const v = String(raw).trim();
    if (!v) continue;
    if (v.length > AUTO_SELECT_MAX_LEN) return null;

    counted += 1;
    seen.add(v);
    // Bail early rather than walking a thousand rows to prove it is free text.
    if (seen.size > AUTO_SELECT_MAX) return null;
  }

  if (!seen.size || counted < seen.size * 2) return null;

  return [
    { value: "", label: "All" },
    ...[...seen].sort((a, b) => a.localeCompare(b)).map((v) => ({ value: v, label: v })),
  ];
};

/* What a select filter offers.
 *
 * A declared list wins, because some columns want a fixed vocabulary in a
 * fixed order (Priority reads High / Medium / Low, not alphabetically).
 *
 * Everything else is derived FROM THE ROWS. It used to return [] when a column
 * declared nothing, so any select filter without a hand-written list showed
 * "No records found" over a column visibly full of values — the Team filter on
 * Task Assignment sat above a column of Web App and Database and offered
 * neither. Reading the data is also self-maintaining: a new team appears in
 * the filter the moment a task carries it, with nobody remembering to add it
 * here.
 *
 * Derived from ALL rows, never the filtered ones. Narrowing to the current
 * selection would leave the dropdown offering only the value already chosen,
 * with no way back to the others.
 */
const toFilterOptions = (col, rows = []) => {
  if (col?.filterOptions?.length) return col.filterOptions;

  const declared = col?.options?.length
    ? col.options.map((o) => (typeof o === "object" ? o : { value: o, label: o }))
    : null;

  const opts =
    declared ??
    [
      ...new Set(
        rows
          .map((r) => {
            /* searchValue is what the text filter matches on, so a select
               filter reading anything else could offer a value its own column
               would then fail to find. */
            const v = col?.searchValue ? col.searchValue(r) : r?.[col?.key];
            return v === null || v === undefined ? "" : String(v).trim();
          })
          .filter(Boolean),
      ),
    ]
      .sort((a, b) => a.localeCompare(b))
      .map((v) => ({ value: v, label: v }));

  return opts.some((o) => o.value === "")
    ? opts
    : [{ value: "", label: "All" }, ...opts];
};

// Memoized Table Row
const TableRow = React.memo(
  ({ row, columns, columnOrder, hiddenCols, stickyColumns, stickyOffsets, rowClassName, renderActions, colWidths = { colWidths }
  }) => {
    return (
      <tr className={rowClassName ? rowClassName(row) : ""}>
        {columnOrder.map((colKey, index) => {
          const col = columns.find((c) => c.key === colKey);
          if (!col || hiddenCols.includes(colKey)) return null;

          return (
            <td
              key={col.key} style={{
                width: `${colWidths[col.key]}px`,
                minWidth: `${colWidths[col.key]}px`,
                maxWidth: `${colWidths[col.key]}px`,
                ...(stickyColumns.includes(col.key)
                  ? { left: `${stickyOffsets[col.key]}px` }
                  : {}),
              }}
              className={`${col.cellClass || ""} ${stickyColumns.includes(col.key) ? "sticky-col" : ""
                }`}
            >
              {col.key == "action" ? renderActions
                ? renderActions(row)
                : "-"
                : col.render
                  ? col.render(row)
                  : row[col.key] || "-"}
            </td>
          );
        })}
      </tr>
    );
  }
);

const ReportTable = ({
  data,
  columns,
  onExport,
  stickyColumns = [],
  onDataChange,
  onVisibleColumnsChange,
  rowClassName,
  onFilterChange,
  onHeaderFilterChange,
  serverSide = false,
  renderActions,
}) => {
  const [tableData, setTableData] = useState(data);
  // Bumped whenever the data prop changes so the row-entrance stagger replays
  // on fetch / page change (but not on local column-filter typing).
  // const [rowAnimKey, setRowAnimKey] = useState(0);
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [hiddenCols, setHiddenCols] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const [menuView, setMenuView] = useState("general");
  const [columnOrder, setColumnOrder] = useState([]);
  const dragItemKey = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const tableScrollRef = useRef(null);

  /* The column-name row's real height, measured, and published to CSS as
     --sk-head-h.
     *
     * The filter row underneath is sticky too, and pins itself to exactly
     * where this row ends. That number had been a constant — 44px, then a
     * calc derived from the padding and font tokens — and both were wrong,
     * because the row's height is padding plus a line box plus a border, and
     * the line box depends on an inherited line-height nobody declares here.
     * Too small and the header overlapped the filter row, hiding its top edge;
     * too large and a strip of the data rows showed between the two.
     *
     * Measuring is the only version that cannot be wrong, and it is the
     * pattern the layout already uses — MainLayout measures the app header and
     * the tab strip the same way and publishes those as variables too.
     *
     * ResizeObserver rather than a one-off read: the row changes height when
     * columns are shown or hidden, when the window narrows enough to shrink
     * the header font, and on the first paint after a web font loads. */
  const headRowRef = useRef(null);
  const [headH, setHeadH] = useState(0);

  useEffect(() => {
    const el = headRowRef.current;
    if (!el) return undefined;

    const measure = () => setHeadH(el.offsetHeight);
    measure();

    if (typeof ResizeObserver === "undefined") return undefined;

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const menuRef = useRef(null);
  const [colWidths, setColWidths] = useState({});
  const activeResize = useRef(null);

  const [filterList, setFilterList] = useState([]);
  // Header-row rich filters: { key: { op, value } | { start, end } }.
  // Used for client-side filtering AND reported to the parent for server-side.
  const [columnFilters, setColumnFilters] = useState({});
  const [opMenu, setOpMenu] = useState(null); // operator menu: { key, top, left }
  const isFirstHeaderFilter = useRef(true);

  const stickyOffsets = useMemo(() => {
    let left = 0;
    const offsets = {};

    columnOrder.forEach((key) => {
      if (stickyColumns.includes(key)) {
        offsets[key] = left;
        left += colWidths[key] || 150;
      }
    });

    return offsets;
  }, [columnOrder, stickyColumns, colWidths]);

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filterList);
    }
  }, [filterList, onFilterChange]);

  // Report header filter changes to the parent (used for server-side filtering).
  // Skip the initial mount so the parent controls the first (unfiltered) load.
  useEffect(() => {
    if (isFirstHeaderFilter.current) {
      isFirstHeaderFilter.current = false;
      return;
    }
    if (onHeaderFilterChange) {
      onHeaderFilterChange(columnFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  useEffect(() => {
    setTableData(data);
    // setRowAnimKey((k) => k + 1);
  }, [data]);

  useEffect(() => {
    if (columns && columns.length > 0) {
      setColumnOrder((prev) => {
        if (prev.length === columns.length) return prev;
        return columns.map((c) => c.key);
      });
    }
  }, [columns]);

  useEffect(() => {
    if (onVisibleColumnsChange) {
      const visibleColumns = columnOrder
        .map((key) => columns.find((c) => c.key === key))
        .filter((col) => col && !hiddenCols.includes(col.key));
      onVisibleColumnsChange(visibleColumns);
    }
  }, [hiddenCols, columns, columnOrder, onVisibleColumnsChange]);

  useEffect(() => {
    const initialWidths = {};
    columns.forEach((col) => {
      const w = col.width ? parseInt(col.width, 10) : 150;
      initialWidths[col.key] = w;
    });
    setColWidths(initialWidths);
  }, [columns]);

  // --- RESIZE HANDLERS ---
  const onMouseDown = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    activeResize.current = {
      key,
      startX: e.clientX,
      startWidth: colWidths[key] || 150,
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
  };

  const onMouseMove = useCallback((e) => {
    if (!activeResize.current) return;
    const { key, startX, startWidth } = activeResize.current;
    const diff = e.clientX - startX;
    setColWidths((prev) => ({
      ...prev,
      [key]: Math.max(50, startWidth + diff),
    }));
  }, []);

  const onMouseUp = useCallback(() => {
    if (!activeResize.current) return;
    activeResize.current = null;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, [onMouseMove]);

  // --- DRAG AND DROP HANDLERS ---
  const handleDragStart = (e, colKey) => {
    dragItemKey.current = colKey;
    e.dataTransfer.effectAllowed = "move";
    e.target.style.opacity = "0.5";
  };
  const handleDragEnter = (e, targetKey) => {
    if (dragItemKey.current !== targetKey) {
      setDragOverKey(targetKey);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (tableScrollRef.current) {
      const { left, right } = tableScrollRef.current.getBoundingClientRect();
      const mouseX = e.clientX;
      const threshold = 150;
      const scrollSpeed = 20;
      if (mouseX < left + threshold) {
        tableScrollRef.current.scrollLeft -= scrollSpeed;
      } else if (mouseX > right - threshold) {
        tableScrollRef.current.scrollLeft += scrollSpeed;
      }
    }
  };
  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    e.target.style.opacity = "1";
    setDragOverKey(null);
    const sourceKey = dragItemKey.current;
    if (!sourceKey || sourceKey === targetKey) return;
    const oldIndex = columnOrder.indexOf(sourceKey);
    const newIndex = columnOrder.indexOf(targetKey);
    if (oldIndex > -1 && newIndex > -1) {
      const newOrder = [...columnOrder];
      newOrder.splice(oldIndex, 1);
      newOrder.splice(newIndex, 0, sourceKey);
      setColumnOrder(newOrder);
    }
    dragItemKey.current = null;
  };
  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDragOverKey(null);
  };

  // --- MENU HANDLER ---
  const handleMenuClick = useCallback((e, colKey) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    let newTop = rect.bottom + window.scrollY + 5;
    let newLeft = rect.left + window.scrollX;
    const menuWidth = 220;
    if (newLeft + menuWidth > window.innerWidth) {
      newLeft = rect.right + window.scrollX - menuWidth;
    }
    setMenuPos({ top: newTop, left: newLeft });
    setMenuView("general");
    setActiveMenu((prev) => (prev === colKey ? null : colKey));
  }, []);

  const toggleColumnVisibility = useCallback((colKey) => {
    setHiddenCols((prev) => {
      if (prev.includes(colKey)) {
        return prev.filter((k) => k !== colKey);
      } else {
        return [...prev, colKey];
      }
    });
  }, []);

  useEffect(() => {
    const handleGlobalClickOrScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) {
        return;
      }
      setActiveMenu(null);
      setOpMenu(null);
    };
    window.addEventListener("click", handleGlobalClickOrScroll);
    window.addEventListener("scroll", handleGlobalClickOrScroll, true);
    return () => {
      window.removeEventListener("click", handleGlobalClickOrScroll);
      window.removeEventListener("scroll", handleGlobalClickOrScroll, true);
    };
  }, []);

  // --- FIXED SORT FUNCTION ---
  const handleSort = (key, dir) => {
    const sorted = [...tableData].sort((a, b) => {
      const colDef = columns.find((c) => c.key === key);
      let valA = colDef?.sortValue ? colDef.sortValue(a) : a[key];
      let valB = colDef?.sortValue ? colDef.sortValue(b) : b[key];

      // Format as Date if datetime column
      if (colDef?.type === "date" || colDef?.type === "datetime") {
        let dateA = colDef?.filterDateValue ? colDef.filterDateValue(a) : valA;
        let dateB = colDef?.filterDateValue ? colDef.filterDateValue(b) : valB;

        valA = dateA ? new Date(dateA).getTime() : 0;
        valB = dateB ? new Date(dateB).getTime() : 0;
      } else {
        // Safe string format
        if (valA == null) valA = "";
        if (valB == null) valB = "";
        if (typeof valA === "string") valA = valA.toString().toLowerCase().trim();
        if (typeof valB === "string") valB = valB.toString().toLowerCase().trim();
      }

      // FIX: Must explicitly return 0 if values are identical to prevent rendering bugs!
      if (valA === valB) return 0;

      // Ascending or Descending
      if (dir === "asc") {
        return valA > valB ? 1 : -1;
      } else {
        return valA < valB ? 1 : -1;
      }
    });
    setTableData(sorted);
  };

  const handleAddFilter = (colKey) => {
    const colDef = columns.find((c) => c.key === colKey);
    const isDate = colDef?.type === "date" || colDef?.type === "datetime";
    setFilterList([
      ...filterList,
      {
        id: Date.now(),
        column: colKey,
        operator: isDate ? "between" : "contains",
        value: "",
        startDate: "",
        endDate: "",
      },
    ]);
    setShowFilter(true);
    setActiveMenu(null);
  };

  const handleClearAllFilters = () => setFilterList([]);

  // ---- Header-row rich filters (client-side) ----
  const isDateColumn = (col) =>
    col?.filterType === "date" ||
    col?.type === "date" ||
    col?.type === "datetime";

  const setTextFilter = (key, patch) =>
    setColumnFilters((prev) => ({
      ...prev,
      [key]: {
        op: prev[key]?.op || DEFAULT_TEXT_OPERATOR,
        /* `|| ""` would turn an empty ticked list into a string and lose the
           array shape the matcher keys on. */
        value: prev[key]?.value ?? "",
        ...patch,
      },
    }));

  const setDateFilter = (key, start, end) =>
    setColumnFilters((prev) => ({ ...prev, [key]: { start, end } }));

  const openOpMenu = (e, key) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setOpMenu((prev) =>
      prev?.key === key
        ? null
        : {
          key,
          top: r.bottom + window.scrollY + 4,
          left: r.left + window.scrollX,
        }
    );
  };

  // --- FILTERING LOGIC ---
  /* One pass over the rows per column, not one per render of every filter
     cell. Keyed by column so the lookup at render time is free. */
  const autoOptions = useMemo(() => {
    const map = {};
    (columns || []).forEach((c) => {
      const opts = categoricalOptions(c, tableData);
      if (opts) map[c.key] = opts;
    });
    return map;
  }, [columns, tableData]);

  const filteredData = useMemo(() => {
    return tableData.filter((row) => {
      // When filtering is handled by the server, the returned rows are already
      // filtered — skip the in-browser header matching so numeric filter values
      // (e.g. Rotation/Status codes) don't wrongly exclude rows.
      const matchesHeaderFilter = serverSide
        ? true
        : columnOrder.every((key) => {
          const f = columnFilters[key];
          if (!f) return true;

          const colDef = columns.find((c) => c.key === key);

          // Date range filter
          if (isDateColumn(colDef)) {
            if (!f.start && !f.end) return true;
            const raw = colDef?.filterDateValue
              ? colDef.filterDateValue(row)
              : row[key];
            return isWithinDateRange(raw, f.start, f.end);
          }

          if (colDef?.filterType === "time") {
            if (!f.value) return true;

            const search = f.value.trim();

            // original value from API
            const rawTime = row[key]; // "16:28"

            const [rawHour, rawMinute] = rawTime.split(":");

            // converted value
            const displayTime = convert24To12(rawTime); // "4:28 PM"
            const [displayHour, displayMinute] = displayTime
              .replace(" AM", "")
              .replace(" PM", "")
              .split(":");

            // User entered only hour (4 / 04 / 16)
            if (/^\d{1,2}$/.test(search)) {
              const s = parseInt(search, 10);

              return (
                parseInt(rawHour, 10) === s ||
                parseInt(displayHour, 10) === s
              );
            }

            // User entered hour:minute
            if (/^\d{1,2}:\d{2}$/.test(search)) {
              const [sh, sm] = search.split(":");

              return (
                (parseInt(rawHour, 10) === parseInt(sh, 10) &&
                  rawMinute.startsWith(sm)) ||

                (parseInt(displayHour, 10) === parseInt(sh, 10) &&
                  displayMinute.startsWith(sm))
              );
            }

            return displayTime.toLowerCase().includes(search.toLowerCase());
          }

          /* A multi-select filter: any of the ticked values will do.
           *
           * Tested before the text operators because its value is an ARRAY,
           * and each of those treats what it is given as a string —
           * ["A","B"] would normalise to "a,b" and match nothing. */
          if (f.op === "in" || Array.isArray(f.value)) {
            const picked = Array.isArray(f.value) ? f.value : [f.value];
            if (picked.length === 0) return true;

            const rawIn = colDef?.searchValue ? colDef.searchValue(row) : row[key];
            const vIn = String(rawIn ?? "").trim().toLowerCase();
            return picked.some(
              (p) => String(p ?? "").trim().toLowerCase() === vIn,
            );
          }

          // Text filter with operator (contains, equals, begins with, …)
          if (!f.value) return true;
          let raw = colDef?.searchValue ? colDef.searchValue(row) : row[key];
          if (typeof raw === "boolean") raw = raw ? "yes" : "no";
          return matchTextOperator(raw, f.op || DEFAULT_TEXT_OPERATOR, f.value);
        });

      if (!matchesHeaderFilter) return false;
      // Shared with the report page, which has no grid but the same dialog.
      return matchesAdvancedFilters(row, filterList, columns);
    });
  }, [tableData, filterList, columns, columnFilters, columnOrder, serverSide]);

  useEffect(() => {
    if (onDataChange) {
      onDataChange(filteredData);
    }
  }, [filteredData, onDataChange]);

  return (
    <div className="report-card">
      {/* FILTER PANEL */}
      {showFilter && (
        <FilterPanel
          filterList={filterList}
          setFilterList={setFilterList}
          onClose={() => setShowFilter(false)}
          onClearAll={handleClearAllFilters}
          columns={columns}
          // Feeds the "type to search" suggestions in the Value box.
          data={tableData}
        />
      )}

      {/* TABLE */}
      <div
        className="table-scroll-x"
        ref={tableScrollRef}
        /* Set here rather than on the table, so the filter row's sticky offset
           and the scroll container it sticks inside are the same element's
           concern. Left unset until measured, so the stylesheet's fallback
           covers the first paint. */
        style={headH ? { "--sk-head-h": `${headH}px` } : undefined}
      >
        <table
          className="datagrid-table"
          style={{
            width: "fit-content",
            minWidth: "100%",
            tableLayout: "fixed",
          }}
        >
          <thead>
            <tr ref={headRowRef}>
              {columnOrder.map((colKey, index) => {
                const col = columns.find((c) => c.key === colKey);
                if (!col || hiddenCols.includes(colKey)) return null;
                const isOver = dragOverKey === col.key;

                return (
                  <th
                    key={col.key}
                    /* No rowSpan. The first header cell used to span the
                       filter row as well, and the filter row skipped index 0
                       to match — so the first column had no filter at all.
                       Which column that was is whatever happens to be first,
                       and these columns are drag-reorderable, so the
                       unfilterable one moved as people rearranged them. */
                    style={{
                      width: `${colWidths[col.key]}px`,
                      minWidth: `${colWidths[col.key]}px`,
                      maxWidth: `${colWidths[col.key]}px`,
                      ...(stickyColumns.includes(col.key)
                        ? { left: stickyOffsets[col.key] }
                        : {}),
                      cursor: "grab",
                      borderLeft: isOver ? "3px solid #5b5fef" : "none",
                      backgroundColor: isOver ? "#eef0fe" : "",
                    }}
                    className={
                      stickyColumns.includes(col.key) ? "sticky-col" : ""
                    }
                    draggable
                    onDragStart={(e) => handleDragStart(e, col.key)}
                    onDragEnter={(e) => handleDragEnter(e, col.key)}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, col.key)}
                    onDragEnd={handleDragEnd}
                  >
                    <div
                      className="th-container"
                      style={{ pointerEvents: "none" }}
                    >
                      <span className="text-truncate">{col.label}</span>
                      {filterList.some((f) => f.column === col.key) && (
                        <i
                          className="bi bi-funnel-fill text-warning ms-1"
                          style={{ fontSize: "0.7rem" }}
                        ></i>
                      )}
                      <button
                        className="header-menu-btn"
                        style={{ pointerEvents: "auto" }}
                        onClick={(e) => handleMenuClick(e, col.key)}
                      >
                        <i className="bi bi-three-dots-vertical"></i>
                      </button>
                    </div>
                    <div
                      className="resizer"
                      onMouseDown={(e) => onMouseDown(e, col.key)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </th>
                );
              })}
            </tr>
            <tr className="table-filter-row">
              {columnOrder.map((colKey) => {
                const col = columns.find((c) => c.key === colKey);

                if (!col || hiddenCols.includes(colKey)) return null;

                return (
                  // <th
                  //   key={col.key}
                  //   // rowSpan={index === 0 ? 2 : 1}
                  //   style={{
                  //     width: `${colWidths[col.key]}px`,
                  //     minWidth: `${colWidths[col.key]}px`,
                  //     ...(stickyColumns.includes(col.key)
                  //       ? { left: stickyOffsets[col.key] }
                  //       : {}),
                  //   }}
                  // >
                  <th
                    key={col.key}
                    className={stickyColumns.includes(col.key) ? "sticky-col" : ""}
                    style={{
                      width: `${colWidths[col.key]}px`,
                      minWidth: `${colWidths[col.key]}px`,
                      ...(stickyColumns.includes(col.key)
                        ? { left: stickyOffsets[col.key] }
                        : {}),
                    }}
                  >
                    <div className="filter-box">
                      {isDateColumn(col) ? (

                        <ColumnDateFilter
                          value={columnFilters[col.key] || {}}
                          onChange={(s, e2) => setDateFilter(col.key, s, e2)}
                        />

                      ) : col.filterType === "time" ? (

                        <input
                          type="time"
                          className="column-filter-input"
                          // placeholder="4 or 4:15"
                          value={columnFilters[col.key]?.value || ""}
                          onChange={(e) =>
                            setTextFilter(col.key, {
                              op: "equals",
                              value: e.target.value,
                            })
                          }
                        />

                      ) : col.filterType === "select" || autoOptions[col.key] ? (

                        <SkSelect
                          width="100%"
                          multiple
                          /* An array, under `op: "in"` — the shape the matcher
                             above looks for. A filter arriving as a single
                             value is still understood, so anything that sets
                             one from elsewhere keeps working. */
                          value={
                            Array.isArray(columnFilters[col.key]?.value)
                              ? columnFilters[col.key].value
                              : columnFilters[col.key]?.value
                              ? [columnFilters[col.key].value]
                              : []
                          }
                          /* tableData, not filteredData — see toFilterOptions.
                             The empty "All" row that builder prepends is
                             dropped here: as a checkbox it would tick a blank
                             value that matches no row, and "none ticked"
                             already means all. */
                          options={(
                            autoOptions[col.key] ?? toFilterOptions(col, tableData)
                          ).filter((o) => o.value !== "")}
                          onChange={(value) =>
                            setTextFilter(col.key, {
                              op: "in",
                              value,
                            })
                          }
                          ariaLabel={`Filter ${col.label}`}
                          searchable
                          placeholder="All"
                          searchPlaceholder="Type to search…"
                        />

                      ) : (

                        <div className="cf-text">
                          <button
                            type="button"
                            className="cf-op"
                            title={getOperatorMeta(columnFilters[col.key]?.op).label}
                            onClick={(e) => openOpMenu(e, col.key)}
                          >
                            <span className="cf-op-glyph">
                              {getOperatorMeta(columnFilters[col.key]?.op).glyph}
                            </span>
                          </button>

                          <input
                            className="column-filter-input"
                            value={columnFilters[col.key]?.value || ""}
                            onChange={(e) =>
                              setTextFilter(col.key, {
                                value: e.target.value,
                              })
                            }
                          />
                        </div>

                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody /*key={rowAnimKey}*/ >
            {filteredData.length > 0 ? (
              filteredData.map((row, idx) => (
                <TableRow
                  key={idx}
                  row={row}
                  columns={columns}
                  columnOrder={columnOrder}
                  hiddenCols={hiddenCols}
                  stickyColumns={stickyColumns}
                  stickyOffsets={stickyOffsets}
                  rowClassName={rowClassName}
                  renderActions={renderActions}
                  colWidths={colWidths}

                />
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-5 text-muted"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {/* <div className="table-wave">
  <svg
    viewBox="0 0 1440 180"
    preserveAspectRatio="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#d9e9ff"
      fillOpacity="0.8"
      d="M0,96L60,112C120,128,240,160,360,154.7C480,149,600,107,720,101.3C840,96,960,128,1080,133.3C1200,139,1320,117,1380,106.7L1440,96V180H0Z"
    />

    <path
      fill="#b9d6ff"
      fillOpacity="0.7"
      d="M0,120L60,133.3C120,147,240,173,360,165.3C480,157,600,117,720,106.7C840,96,960,117,1080,138.7C1200,160,1320,181,1380,192L1440,203V180H0Z"
    />
  </svg>
</div> */}
      </div>

      {/* OPERATOR MENU PORTAL */}
      {opMenu &&
        ReactDOM.createPortal(
          <div
            className="cf-op-menu"
            style={{ top: opMenu.top, left: opMenu.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {TEXT_OPERATORS.map((op) => {
              const current =
                columnFilters[opMenu.key]?.op || DEFAULT_TEXT_OPERATOR;
              return (
                <button
                  key={op.value}
                  type="button"
                  className={`cf-op-item ${op.value === current ? "active" : ""
                    }`}
                  onClick={() => {
                    setTextFilter(opMenu.key, { op: op.value });
                    setOpMenu(null);
                  }}
                >
                  <span className="cf-op-glyph">{op.glyph}</span>
                  <span>{op.label}</span>
                </button>
              );
            })}
          </div>,
          document.body
        )}

      {/* MENU PORTAL */}
      {activeMenu &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="header-dropdown-menu shadow-lg"
            style={{
              position: "absolute",
              top: menuPos.top,
              left: menuPos.left,
              minWidth: "220px",
              backgroundColor: "white",
              borderRadius: "0.5rem",
              zIndex: 99999,
              border: "1px solid rgba(0,0,0,0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {menuView === "general" ? (
              <div className="py-2">
                <div
                  className="dropdown-item px-3 py-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort(activeMenu, "asc")}
                >
                  <i className="bi bi-sort-alpha-down me-2"></i>{" "}
                  <span>Sort A to Z</span>
                </div>
                <div
                  className="dropdown-item px-3 py-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleSort(activeMenu, "desc")}
                >
                  <i className="bi bi-sort-alpha-down-alt me-2"></i>{" "}
                  <span>Sort Z to A</span>
                </div>
                <div className="dropdown-divider my-1 border-top"></div>
                <div
                  className="dropdown-item px-3 py-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleAddFilter(activeMenu)}
                >
                  <i className="bi bi-funnel me-2"></i>{" "}
                  <span>Filter Column</span>
                </div>
                <div className="dropdown-divider my-1 border-top"></div>
                <div
                  className="dropdown-item px-3 py-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => setMenuView("columns")}
                >
                  <i className="bi bi-layout-three-columns me-2"></i>
                  <span>Column Chooser</span>
                </div>
                <div
                  className="dropdown-item px-3 py-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    setHiddenCols((prev) => [...prev, activeMenu]);
                    setActiveMenu(null);
                  }}
                >
                  <i className="bi bi-eye-slash me-2"></i>{" "}
                  <span>Hide This Column</span>
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column py-2">
                <div
                  className="dropdown-item px-3 py-2 text-secondary fw-bold"
                  style={{ cursor: "pointer" }}
                  onClick={() => setMenuView("general")}
                >
                  <i className="bi bi-arrow-left me-2"></i> <span>Back</span>
                </div>
                <div className="dropdown-divider my-1 border-top"></div>
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {columns.map((col) => (
                    <div
                      key={col.key}
                      className="px-3 py-1 d-flex align-items-center dropdown-item-hover"
                    >
                      <input
                        className="form-check-input mt-0 me-2"
                        type="checkbox"
                        id={`col-chk-${col.key}`}
                        checked={!hiddenCols.includes(col.key)}
                        onChange={() => toggleColumnVisibility(col.key)}
                        style={{ cursor: "pointer" }}
                      />
                      <label
                        className="form-check-label small text-truncate"
                        htmlFor={`col-chk-${col.key}`}
                        title={col.label}
                        style={{
                          cursor: "pointer",
                          userSelect: "none",
                          flex: 1,
                        }}
                      >
                        {col.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ReportTable;